import React, { useState, useEffect, useMemo } from 'react'
import { ClassificationResult, FeatureImportance, ModelMetrics } from '../../types/ml'
import ROCCurveChart from '../../components/charts/ROCCurveChart'

interface EquipmentHealth {
  equipment_id: string
  predicted_label: string
  probability: number
  time_to_failure?: number
  last_updated: string
}

const Classification: React.FC = () => {
  const [equipmentHealth, setEquipmentHealth] = useState<EquipmentHealth[]>([])
  const [selectedEquipment, setSelectedEquipment] = useState<string | null>(null)
  const [classificationResult, setClassificationResult] = useState<ClassificationResult | null>(null)
  const [confusionMatrix, setConfusionMatrix] = useState<number[][]>([])
  const [modelMetrics, setModelMetrics] = useState<ModelMetrics | null>(null)
  const [probabilityThreshold, setProbabilityThreshold] = useState(0.7)
  const [selectedModel, setSelectedModel] = useState('xgboost')

  const mockEquipmentHealth = useMemo(() => [
    {
      equipment_id: 'INV-01',
      predicted_label: 'normal',
      probability: 0.92,
      last_updated: new Date().toISOString()
    },
    {
      equipment_id: 'INV-02',
      predicted_label: 'degradation',
      probability: 0.78,
      time_to_failure: 72,
      last_updated: new Date().toISOString()
    },
    {
      equipment_id: 'INV-03',
      predicted_label: 'sensor_fault',
      probability: 0.85,
      last_updated: new Date().toISOString()
    },
    {
      equipment_id: 'INV-04',
      predicted_label: 'grid_fault',
      probability: 0.65,
      last_updated: new Date().toISOString()
    },
    {
      equipment_id: 'INV-05',
      predicted_label: 'normal',
      probability: 0.88,
      last_updated: new Date().toISOString()
    }
  ], []);

  const mockMetrics = useMemo(() => ({
    rmse: 0,
    mae: 0,
    mape: 0,
    r_squared: 0,
    accuracy: 0.89,
    precision: 0.87,
    recall: 0.84,
    f1_score: 0.85
  }), []);

  const mockMatrix = useMemo(() => [
    [45, 2, 1, 0],
    [3, 38, 2, 1],
    [1, 1, 42, 2],
    [0, 2, 1, 41]
  ], []);

  useEffect(() => {
    setEquipmentHealth(mockEquipmentHealth)
    setModelMetrics(mockMetrics)
    setConfusionMatrix(mockMatrix)
  }, [mockEquipmentHealth, mockMetrics, mockMatrix])



  const handleEquipmentClick = async (equipmentId: string) => {
    setSelectedEquipment(equipmentId)
    
    // Mock classification result with explanation
    const mockResult: ClassificationResult = {
      label: 'degradation',
      probabilities: {
        'normal': 0.15,
        'degradation': 0.78,
        'sensor_fault': 0.05,
        'grid_fault': 0.02
      },
      explanation: [
        { feature: 'power_std_6h', importance: 0.34, impact: 'positive' },
        { feature: 'voltage_mean_1h', importance: 0.28, impact: 'negative' },
        { feature: 'temp_max_24h', importance: 0.22, impact: 'positive' },
        { feature: 'efficiency_trend', importance: 0.16, impact: 'negative' }
      ],
      confidence: 0.78
    }
    setClassificationResult(mockResult)
  }

  const createMaintenanceTicket = (equipmentId: string) => {
    // Mock maintenance ticket creation
    alert(`Maintenance ticket created for ${equipmentId}`)
  }

  const getLabelColor = (label: string) => {
    switch (label) {
      case 'normal': return 'text-green-600 bg-green-100'
      case 'degradation': return 'text-yellow-600 bg-yellow-100'
      case 'sensor_fault': return 'text-orange-600 bg-orange-100'
      case 'grid_fault': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const classes = ['normal', 'degradation', 'sensor_fault', 'grid_fault']

  return (
    <div className="p-6 space-y-6">
      {/* Header & Controls */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Equipment Classification</h1>
        <div className="flex gap-4">
          <select 
            value={selectedModel} 
            onChange={(e) => setSelectedModel(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            <option value="xgboost">XGBoost</option>
            <option value="random_forest">Random Forest</option>
            <option value="neural_net">Neural Network</option>
          </select>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Threshold:</label>
            <input
              type="range"
              min="0.1"
              max="0.9"
              step="0.1"
              value={probabilityThreshold}
              onChange={(e) => setProbabilityThreshold(parseFloat(e.target.value))}
              className="w-20"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">{probabilityThreshold}</span>
          </div>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Retrain Model
          </button>
        </div>
      </div>

      {/* Model Performance KPIs */}
      {modelMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Accuracy</h3>
            <p className="text-2xl font-bold text-blue-600">{(modelMetrics.accuracy! * 100).toFixed(1)}%</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Precision</h3>
            <p className="text-2xl font-bold text-green-600">{(modelMetrics.precision! * 100).toFixed(1)}%</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Recall</h3>
            <p className="text-2xl font-bold text-purple-600">{(modelMetrics.recall! * 100).toFixed(1)}%</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">F1 Score</h3>
            <p className="text-2xl font-bold text-orange-600">{(modelMetrics.f1_score! * 100).toFixed(1)}%</p>
          </div>
        </div>
      )}

      {/* Equipment Health Dashboard */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Equipment Health Status</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Equipment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Predicted Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Probability
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Time to Failure
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {equipmentHealth
                .filter(eq => eq.probability >= probabilityThreshold)
                .map((equipment) => (
                <tr 
                  key={equipment.equipment_id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={() => handleEquipmentClick(equipment.equipment_id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {equipment.equipment_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getLabelColor(equipment.predicted_label)}`}>
                      {equipment.predicted_label.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {(equipment.probability * 100).toFixed(1)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {equipment.time_to_failure ? `${equipment.time_to_failure}h` : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {equipment.predicted_label !== 'normal' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          createMaintenanceTicket(equipment.equipment_id)
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Create Ticket
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confusion Matrix & ROC Curves */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Confusion Matrix */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Confusion Matrix</h3>
          <div className="grid grid-cols-4 gap-1 text-xs">
            {confusionMatrix.map((row, i) => 
              row.map((value, j) => (
                <div
                  key={`${i}-${j}`}
                  className={`p-2 text-center rounded ${
                    i === j ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}
                  style={{ 
                    backgroundColor: i === j 
                      ? `rgba(34, 197, 94, ${value / 50})` 
                      : `rgba(239, 68, 68, ${value / 10})`
                  }}
                >
                  {value}
                </div>
              ))
            )}
          </div>
          <div className="mt-2 flex justify-between text-xs text-gray-500">
            {classes.map(cls => (
              <span key={cls}>{cls}</span>
            ))}
          </div>
        </div>

        {/* ROC Curves */}
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            ROC Curves 📊
          </h3>
          <ROCCurveChart />
        </div>
      </div>

      {/* Feature Importance & Case Details */}
      {selectedEquipment && classificationResult && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Feature Importance */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">
              Feature Importance - {selectedEquipment}
            </h3>
            <div className="space-y-3">
              {classificationResult.explanation.map((feature, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium">{feature.feature}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          feature.impact === 'positive' ? 'bg-red-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${feature.importance * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-500">{(feature.importance * 100).toFixed(1)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Class Probabilities */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Class Probabilities</h3>
            <div className="space-y-3">
              {Object.entries(classificationResult.probabilities).map(([label, prob]) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-sm font-medium capitalize">{label.replace('_', ' ')}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${prob * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-500">{(prob * 100).toFixed(1)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Classification